import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Shared responder map state spanning tours and zones.
 * Tracks multi-selection and layer isolation so one map can drive both features.
 */
const ResponderMapContext = createContext(null);

const coerceId = (id) => (id == null ? null : `${id}`);

const loadShowZoneTools = () => {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem('responder.showZoneTools');
    if (raw === null) return true;
    return raw === 'true';
  } catch (e) {
    return true;
  }
};

const loadShowTourTools = () => {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem('responder.showTourTools');
    if (raw === null) return true;
    return raw === 'true';
  } catch (e) {
    return true;
  }
};

const loadShowZoneEditor = () => {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem('responder.showZoneEditor');
    if (raw === null) return true;
    return raw === 'true';
  } catch (e) {
    return true;
  }
};

const loadShowAllZones = () => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem('responder.zones.showAllOnMap');
    if (raw === null) return false;
    return raw === 'true';
  } catch (e) {
    return false;
  }
};

/**
 * Load the persisted zone anchor visibility preference.
 * @returns {boolean}
 */
const loadShowZoneAnchors = () => {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem('responder.showZoneAnchors');
    if (raw === null) return true;
    return raw === 'true';
  } catch (e) {
    return true;
  }
};

export const ResponderMapProvider = ({ children }) => {
  const [selectedTourIds, setSelectedTourIds] = useState([]);
  const [selectedZoneIds, setSelectedZoneIds] = useState([]);
  const [isolatedLayerDepth, setIsolatedLayerDepth] = useState(null);
  const [showZoneTools, setShowZoneTools] = useState(loadShowZoneTools);
  const [showTourTools, setShowTourTools] = useState(loadShowTourTools);
  const [showZoneEditor, setShowZoneEditor] = useState(loadShowZoneEditor);
  const [showAllZones, setShowAllZones] = useState(loadShowAllZones);
  // Persisted toggle for zone anchor visibility.
  const [showZoneAnchors, setShowZoneAnchors] = useState(loadShowZoneAnchors);
  const [zonePlacementPreview, setZonePlacementPreview] = useState(null);

  const toggleTourSelection = useCallback((id) => {
    const key = coerceId(id);
    if (key == null) return;
    setSelectedTourIds((prev) =>
      prev.some((t) => `${t}` === key) ? prev.filter((t) => `${t}` !== key) : [...prev, id],
    );
  }, []);

  const toggleZoneSelection = useCallback((id) => {
    const key = coerceId(id);
    if (key == null) return;
    setSelectedZoneIds((prev) =>
      prev.some((z) => `${z}` === key) ? prev.filter((z) => `${z}` !== key) : [...prev, id],
    );
  }, []);

  const clearIsolation = useCallback(() => setIsolatedLayerDepth(null), []);

  useEffect(() => {
    try {
      window.localStorage.setItem('responder.showZoneTools', showZoneTools ? 'true' : 'false');
    } catch (e) {
      // ignore storage errors
    }
  }, [showZoneTools]);

  useEffect(() => {
    try {
      window.localStorage.setItem('responder.showTourTools', showTourTools ? 'true' : 'false');
    } catch (e) {
      // ignore storage errors
    }
  }, [showTourTools]);

  useEffect(() => {
    try {
      window.localStorage.setItem('responder.showZoneEditor', showZoneEditor ? 'true' : 'false');
    } catch (e) {
      // ignore storage errors
    }
  }, [showZoneEditor]);

  useEffect(() => {
    try {
      window.localStorage.setItem('responder.zones.showAllOnMap', showAllZones ? 'true' : 'false');
    } catch (e) {
      // ignore storage errors
    }
  }, [showAllZones]);

  useEffect(() => {
    try {
      window.localStorage.setItem('responder.showZoneAnchors', showZoneAnchors ? 'true' : 'false');
    } catch (e) {
      // ignore storage errors
    }
  }, [showZoneAnchors]);

  const value = useMemo(
    () => ({
      selectedTourIds,
      setSelectedTourIds,
      toggleTourSelection,
      selectedZoneIds,
      setSelectedZoneIds,
      toggleZoneSelection,
      isolatedLayerDepth,
      setIsolatedLayerDepth,
      clearIsolation,
      showZoneTools,
      setShowZoneTools,
      showTourTools,
      setShowTourTools,
      showZoneEditor,
      setShowZoneEditor,
      showAllZones,
      setShowAllZones,
      showZoneAnchors,
      setShowZoneAnchors,
      zonePlacementPreview,
      setZonePlacementPreview,
    }),
    [
      clearIsolation,
      isolatedLayerDepth,
      selectedTourIds,
      selectedZoneIds,
      toggleTourSelection,
      toggleZoneSelection,
      showZoneTools,
      showTourTools,
      showZoneEditor,
      showAllZones,
      showZoneAnchors,
      zonePlacementPreview,
    ],
  );

  return <ResponderMapContext.Provider value={value}>{children}</ResponderMapContext.Provider>;
};

export const useResponderMap = () => {
  const ctx = useContext(ResponderMapContext);
  if (!ctx) throw new Error('useResponderMap must be used within a ResponderMapProvider');
  return ctx;
};

export { ResponderMapContext };
